import * as React from "react"
import {useCallback, useState} from "react"
import {Skeleton, Button, Box, FormControl, InputLabel, Select, MenuItem, Toolbar, Typography, IconButton, Tooltip, Menu, Avatar, CircularProgress} from "@mui/material"
import {TableSeeker} from "@ui-seeker/core"
import {useAppToast} from "@ui-seeker/app"
import {deleteSubmit, postSubmit} from "@ui-seeker/utils"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import PersonAddIcon from "@mui/icons-material/PersonAdd"

import {BACKEND_APIS} from "@constants"

import {isLoadingBatches, batchesTableData, filteredBatchesTableData, selectedBatchForDetails, selectedBatchForItems, isCreateBatchDrawerOpen, selectedBatchForAutovalidate, batchStatusFilter, batchOriginFilter, batchCustomerFilter, batchCustomerOptions, batchesTableState, selectedBatchIds, activeBatchTab} from "../@signals"
import {useBatchesData, useUsers} from "../@hooks"
import SecondPassDialog from "../SecondPassDialog"
import BatchesSummaryCards from "./SummaryCards"
import {
	batchIDColumn,
	nameColumn,
	statusColumn,
	autoTasksColumn,
	qaTasksColumn,
	automappedEanColumn,
	automappedIaColumn,
	autoexcludedColumn,
	pendingTasksColumn,
	autoMappingPctColumn,
	resultsColumn,
	createdAtColumn,
	userColumn,
	customerColumn,
	userIDColumn,
	autovalidatedColumn,
	actionsColumn,
	secondPassColumn,
	totalTasksColumn,
	completedTasksColumn,
	failedTasksColumn,
	progressColumn
} from "./columns"


export default function BatchesTable() {

    const { isEmpty } = useBatchesData(true)
	const { users } = useUsers()
	const { showToast } = useAppToast()
	const [deletingBatchID, setDeletingBatchID] = useState(null)
	const [finalizingBatchID, setFinalizingBatchID] = useState(null)
	const [resettingBatchID, setResettingBatchID] = useState(null)
	const [retryingBatchID, setRetryingBatchID] = useState(null)
	const [aiPassTarget, setAiPassTarget] = useState(null)
	const [isBulkDeleting, setIsBulkDeleting] = useState(false)
	const [isBulkAssigning, setIsBulkAssigning] = useState(false)
	const [bulkAssignMenuAnchor, setBulkAssignMenuAnchor] = useState(null)

	const handleStatusFilterChange = useCallback((event) => {
		batchStatusFilter.value = event.target.value
		selectedBatchIds.value = [] // Clear selection when filter changes
	}, [])

	const handleOriginFilterChange = useCallback((event) => {
		batchOriginFilter.value = event.target.value
		selectedBatchIds.value = []
	}, [])

	const handleCustomerFilterChange = useCallback((event) => {
		batchCustomerFilter.value = event.target.value
		selectedBatchIds.value = []
	}, [])

	const handleTableChange = useCallback((action, tableState) => {
		// Persist table state to signal so it survives data refreshes
		if (action === 'sort' || action === 'changePage' || action === 'changeRowsPerPage' || action === 'search') {
			batchesTableState.value = {
				sortOrder: tableState.sortOrder || batchesTableState.value.sortOrder,
				page: tableState.page ?? batchesTableState.value.page,
				rowsPerPage: tableState.rowsPerPage ?? batchesTableState.value.rowsPerPage,
				searchText: tableState.searchText ?? batchesTableState.value.searchText
			}
		}
	}, [])

	const handleViewDetails = useCallback((batchID) => {
		// Store the batchID directly instead of the index
		selectedBatchForDetails.value = batchID
	}, [])

	const handleViewAllItems = useCallback((batchID, batchName) => {
		// Check if this batch is already selected
		if (selectedBatchForItems.value && selectedBatchForItems.value.batchID === batchID) {
			selectedBatchForItems.value = null
			return
		}
		// Set the selected batch with type info
		selectedBatchForItems.value = {
			batchID,
			batchName,
			batchType: activeBatchTab.value
		}
	}, [])

	const handleAutovalidate = useCallback((batchID) => {
		// Store the batchID directly instead of the index
		selectedBatchForAutovalidate.value = batchID
	}, [])

	const handleCreateBatch = useCallback(() => {
		isCreateBatchDrawerOpen.value = true
	}, [])

	const handleAiPass = useCallback((batchID, batchName) => {
		setAiPassTarget({ batchID, batchName })
	}, [])

	const handleForceFinalize = useCallback((batchID, batchName) => {
		showToast.confirm(
			`Are you sure you want to force finalize batch #${batchID} "${batchName}"? This will mark the batch as ready even if it's still being created.`,
			{
				onSubmit: async () => {
					setFinalizingBatchID(batchID)
					try {
						await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchID}/finalize`, {})
						
						// Update batch status in table data
						batchesTableData.value = batchesTableData.value.map(batch => 
							batch.batchID === batchID 
								? { ...batch, status: 'ready' }
								: batch
						)
						
						showToast.success('Batch finalized successfully')
					} catch (err) {
						console.error('Failed to finalize batch:', err)
						showToast.error('Failed to finalize batch')
					} finally {
						setFinalizingBatchID(null)
					}
				}
			}
		)
	}, [showToast])

	const handleRetryTasks = useCallback((batchID, batchName, pending) => {
		const pendingLabel = pending > 0 ? `${pending} pending task(s)` : 'the pending tasks'
		showToast.confirm(
			`Re-enqueue ${pendingLabel} of batch #${batchID} "${batchName}"? Cloud Tasks gives up on a task after a few seconds, so tasks lost to an outage never come back on their own. This re-derives what is still pending and runs it again — it costs real AI calls.`,
			{
				onSubmit: async () => {
					setRetryingBatchID(batchID)
					try {
						const res = await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchID}/retry`, {})

						if (res?.status === 'finalized') {
							batchesTableData.value = batchesTableData.value.map(batch =>
								batch.batchID === batchID
									? { ...batch, status: 'ready' }
									: batch
							)
							showToast.success('Nothing left to run — batch closed')
						} else {
							showToast.success(`Re-enqueued ${res?.enqueued ?? res?.pending ?? 0} task(s)`)
						}

						// The counters live in the normalizer; trigger a full reload
						// instead of guessing them here.
						isLoadingBatches.value = true
					} catch (err) {
						console.error('Failed to retry batch tasks:', err)
						// A failed batch whose selection still resolves to nothing comes
						// back as 400 with the reason; say it, and reload so the FAILED
						// chip carries the fresh reason too.
						const body = err?.response?.data || err?.data
						showToast.error(body?.message || err?.message || 'Failed to retry batch tasks')
						isLoadingBatches.value = true
					} finally {
						setRetryingBatchID(null)
					}
				}
			}
		)
	}, [showToast])

	const handleResetExcluded = useCallback((batchID, batchName, autoexcluded) => {
		showToast.confirm(
			`Reset ${autoexcluded} auto-excluded items to pending for batch #${batchID} "${batchName}"?`,
			{
				onSubmit: async () => {
					setResettingBatchID(batchID)
					try {
						const res = await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchID}/reset-excluded`, {})
						const resetCount = res?.resetCount || 0

						// Trigger full reload to update all counters
						isLoadingBatches.value = true

						showToast.success(`${resetCount} items reset to pending`)
					} catch (err) {
						console.error('Failed to reset excluded items:', err)
						showToast.error('Failed to reset excluded items')
					} finally {
						setResettingBatchID(null)
					}
				}
			}
		)
	}, [showToast])

	const handleDeleteBatch = useCallback((batchID, batchName) => {
		showToast.confirm(
			`Are you sure you want to delete batch #${batchID} "${batchName}"? This will delete the batch and all associated SKUs to be mapped.`,
			{
				onSubmit: async () => {
					setDeletingBatchID(batchID)
					try {
						await deleteSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchID}`)
						
						// Remove batch from table data
						batchesTableData.value = batchesTableData.value.filter(
							batch => batch.batchID !== batchID
						)
						
						showToast.success('Batch deleted successfully')
					} catch (err) {
						console.error('Failed to delete batch:', err)
						showToast.error('Failed to delete batch')
					} finally {
						setDeletingBatchID(null)
					}
				}
			}
		)
	}, [showToast])

	// Bulk action handlers
	const handleRowSelectionChange = useCallback((currentRowsSelected, allRowsSelected, rowsSelected) => {
		// rowsSelected contains the indices of selected rows
		const selectedIds = rowsSelected.map(rowIndex => {
			const batch = filteredBatchesTableData.value[rowIndex]
			return batch?.batchID
		}).filter(Boolean)
		selectedBatchIds.value = selectedIds
	}, [])

	const handleBulkDelete = useCallback(() => {
		const count = selectedBatchIds.value.length
		showToast.confirm(
			`Are you sure you want to delete ${count} batch${count > 1 ? 'es' : ''}? This will delete all associated mappings.`,
			{
				onSubmit: async () => {
					setIsBulkDeleting(true)
					const deletedIds = []
					let errorCount = 0

					for (const batchID of selectedBatchIds.value) {
						try {
							await deleteSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchID}`)
							deletedIds.push(batchID)
						} catch (err) {
							console.error(`Failed to delete batch ${batchID}:`, err)
							errorCount++
						}
					}

					// Remove only successfully deleted batches from table data
					batchesTableData.value = batchesTableData.value.filter(
						batch => !deletedIds.includes(batch.batchID)
					)

					// Clear selection
					selectedBatchIds.value = []
					setIsBulkDeleting(false)

					if (errorCount === 0) {
						showToast.success(`${deletedIds.length} batch${deletedIds.length > 1 ? 'es' : ''} deleted successfully`)
					} else {
						showToast.warning(`${deletedIds.length} deleted, ${errorCount} failed`)
					}
				}
			}
		)
	}, [showToast])

	const handleBulkAssignClick = useCallback((event) => {
		setBulkAssignMenuAnchor(event.currentTarget)
	}, [])

	const handleBulkAssignClose = useCallback(() => {
		setBulkAssignMenuAnchor(null)
	}, [])

	const handleBulkAssignUser = useCallback(async (userID, userName) => {
		handleBulkAssignClose()
		setIsBulkAssigning(true)
		let successCount = 0
		let errorCount = 0
		const isUnassign = userID === null

		for (const batchID of selectedBatchIds.value) {
			try {
				await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchID}/assign-user`, { userID })
				successCount++
			} catch (err) {
				console.error(`Failed to ${isUnassign ? 'unassign' : 'assign'} user ${isUnassign ? 'from' : 'to'} batch ${batchID}:`, err)
				errorCount++
			}
		}

		// Update table data
		batchesTableData.value = batchesTableData.value.map(batch =>
			selectedBatchIds.value.includes(batch.batchID)
				? { ...batch, userID, userName }
				: batch
		)

		// Clear selection
		selectedBatchIds.value = []
		setIsBulkAssigning(false)

		const actionText = isUnassign ? 'unassigned' : `assigned to ${userName}`
		if (errorCount === 0) {
			showToast.success(`${successCount} batch${successCount > 1 ? 'es' : ''} ${actionText}`)
		} else {
			showToast.warning(`${successCount} ${actionText}, ${errorCount} failed`)
		}
	}, [showToast])

	// Get initials from user name (for bulk assign menu)
	const getInitials = (name) => {
		if (!name) return '?'
		const parts = name.trim().split(' ')
		if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
	}

	const stringToColor = (string) => {
		if (!string) return '#757575'
		let hash = 0
		for (let i = 0; i < string.length; i++) {
			hash = string.charCodeAt(i) + ((hash << 5) - hash)
		}
		const hue = hash % 360
		return `hsl(${hue}, 60%, 50%)`
	}

	if (isLoadingBatches.value) return <Skeleton variant="rectangular" height={500} sx={{ mt: 2 }} />

	return (
		<Box>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
				<FormControl size="small" sx={{ minWidth: 180 }}>
					<InputLabel id="batch-status-filter-label">Status Filter</InputLabel>
					<Select
						labelId="batch-status-filter-label"
						id="batch-status-filter"
						value={batchStatusFilter.value}
						label="Status Filter"
						onChange={handleStatusFilterChange}
					>
						<MenuItem value="active">Active Only</MenuItem>
						<MenuItem value="finished">Finished Only</MenuItem>
						<MenuItem value="all">All Batches</MenuItem>
					</Select>
				</FormControl>
				<FormControl size="small" sx={{ minWidth: 180, ml: 2 }}>
					<InputLabel id="batch-origin-filter-label">Origin</InputLabel>
					<Select
						labelId="batch-origin-filter-label"
						id="batch-origin-filter"
						value={batchOriginFilter.value}
						label="Origin"
						onChange={handleOriginFilterChange}
					>
						<MenuItem value="all">Any Origin</MenuItem>
						<MenuItem value="manual">Manual Only</MenuItem>
						<MenuItem value="recurrent">Recurrent Only</MenuItem>
					</Select>
				</FormControl>
				{/* Applied server-side: the customer lives inside the batch config, so the
				    table cannot filter on it. Picking one hides raw2sku entirely — that
				    direction has no customer to belong to. */}
				<FormControl size="small" sx={{ minWidth: 200, ml: 2 }}>
					<InputLabel id="batch-customer-filter-label">Customer</InputLabel>
					<Select
						labelId="batch-customer-filter-label"
						id="batch-customer-filter"
						value={batchCustomerFilter.value}
						label="Customer"
						onChange={handleCustomerFilterChange}
					>
						<MenuItem value="all">Any Customer</MenuItem>
						{batchCustomerOptions.value.map(opt => (
							<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
						))}
					</Select>
				</FormControl>
				<Box sx={{ flexGrow: 1 }} />
				<Button
					variant="contained"
					color="primary"
					startIcon={<AddIcon />}
					onClick={handleCreateBatch}
				>
					Create New Batch
				</Button>
			</Box>

			<BatchesSummaryCards />

			{/* Bulk Actions Toolbar */}
			{selectedBatchIds.value.length > 0 && (
				<Toolbar
					sx={{
						bgcolor: 'primary.lighter',
						borderRadius: 1,
						mb: 2,
						display: 'flex',
						justifyContent: 'space-between'
					}}
				>
					<Typography color="primary" fontWeight="medium">
						{selectedBatchIds.value.length} batch{selectedBatchIds.value.length > 1 ? 'es' : ''} selected
					</Typography>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Tooltip title="Assign User">
							<IconButton
								color="primary"
								onClick={handleBulkAssignClick}
								disabled={isBulkAssigning}
							>
								{isBulkAssigning ? <CircularProgress size={24} /> : <PersonAddIcon />}
							</IconButton>
						</Tooltip>
						<Tooltip title="Delete Selected">
							<IconButton
								color="error"
								onClick={handleBulkDelete}
								disabled={isBulkDeleting}
							>
								{isBulkDeleting ? <CircularProgress size={24} /> : <DeleteIcon />}
							</IconButton>
						</Tooltip>
					</Box>
				</Toolbar>
			)}

			{/* Bulk Assign User Menu */}
			<Menu
				anchorEl={bulkAssignMenuAnchor}
				open={Boolean(bulkAssignMenuAnchor)}
				onClose={handleBulkAssignClose}
				PaperProps={{ sx: { maxHeight: 300, width: 250 } }}
			>
				<MenuItem onClick={() => handleBulkAssignUser(null, null)} sx={{ color: 'error.main' }}>
					<Typography variant="body2">Unassign All</Typography>
				</MenuItem>
				<Box sx={{ borderBottom: 1, borderColor: 'divider', my: 1 }} />
				{users && users.length > 0 ? (
					users.map(user => (
						<MenuItem
							key={user.userID}
							onClick={() => handleBulkAssignUser(user.userID, user.userName)}
						>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<Avatar
									sx={{
										width: 24,
										height: 24,
										fontSize: '0.75rem',
										bgcolor: stringToColor(user.userName)
									}}
								>
									{getInitials(user.userName)}
								</Avatar>
								<Typography variant="body2">{user.userName}</Typography>
							</Box>
						</MenuItem>
					))
				) : (
					<MenuItem disabled>
						<Typography variant="body2" color="text.secondary">No users available</Typography>
					</MenuItem>
				)}
			</Menu>

			<TableSeeker
				data={filteredBatchesTableData.value}
				title={'Batches'}
				columns={[
					batchIDColumn(selectedBatchForItems.value?.batchID || null),
					nameColumn(),
					statusColumn(),
					autoTasksColumn(),
					automappedEanColumn(),
					automappedIaColumn(),
					autoexcludedColumn(),
					qaTasksColumn(),
					pendingTasksColumn(),
					autoMappingPctColumn(),
					resultsColumn(),
					createdAtColumn(),
					userColumn(users),
					userIDColumn(),
					autovalidatedColumn(),
					secondPassColumn(activeBatchTab.value !== 'schedules' ? handleAiPass : null),
					// Indices 16/17/18 — actionsColumn and progressColumn read these by
					// position. Append here, never insert earlier in the array.
					totalTasksColumn(),
					completedTasksColumn(),
					failedTasksColumn(),
					progressColumn(),
					// After every index-read column: inserting it earlier shifts rowData[13..18].
					customerColumn(),
					actionsColumn(handleViewDetails, handleViewAllItems, handleAutovalidate, handleForceFinalize, handleResetExcluded, handleDeleteBatch, deletingBatchID, finalizingBatchID, resettingBatchID, selectedBatchForItems.value?.batchID, activeBatchTab.value !== 'schedules' ? handleAiPass : null, handleRetryTasks, retryingBatchID)
				]}
				options={{
					selectableRows: 'multiple',
					selectableRowsHeader: true,
					selectableRowsHideCheckboxes: false,
					selectToolbarPlacement: 'none',
					rowsSelected: filteredBatchesTableData.value
						.map((batch, index) => selectedBatchIds.value.includes(batch.batchID) ? index : -1)
						.filter(index => index !== -1),
					onRowSelectionChange: handleRowSelectionChange,
					sortOrder: batchesTableState.value.sortOrder,
					page: batchesTableState.value.page,
					rowsPerPage: batchesTableState.value.rowsPerPage,
					searchText: batchesTableState.value.searchText,
					onTableChange: handleTableChange,
					enhancedSetRowProps: () => ({ sx: {
						"&:has(.is-open)": {
							backgroundColor: "info.lighter",
							"&:hover": { backgroundColor: "info.light" }
						}
					} })
				}}
				textLabels= {{
					body: {
						noMatch: 'No batches found.',
					}
				}}
			/>
			<SecondPassDialog
				target={aiPassTarget}
				onClose={() => setAiPassTarget(null)}
			/>
		</Box>
	)
}
